import Box from "@/common/components/Box";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextArea from "@/common/components/TextArea";
import { Building2Icon } from "lucide-react";
import NegotiationHelper from "../../components/NegotiationHelper";
import EquipmentCategorySummary from "../../components/EquipmentCategorySummary";
import NegotiatingProducts from "../../components/Create/NegotiatingProducts";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { negotiationSchema, NegotiationValidationSchema } from "../../schemas/negotiation.schema";
import Button from "@/common/components/Button";
import EstablishmentConsumptionEstimativeNote from "../../components/EstablishmentConsumptionEstimativeNote";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import useCreateNegotiation from "../../hooks/useCreateNegotiation";
import { useEffect, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";

const NegotiationsCreate = () => {
  const navigate = useNavigate();

  const { filterParams } = useFilterParams();

  const [negotiationId, setNegotiationId] = useState();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { data: establishment } = useEstablishmentCompany(filterParams.establishment_id);

  const {
    mutate: createNegotiation,
    isError: createNegotiationIsError,
    isPending: createIsPending,
  } = useCreateNegotiation({
    establishmentId: establishment?.id,
    onSuccess: (response) => {
      setMessages(['Negociação enviada com sucesso!']);

      if (response.data.data.id) {
        setNegotiationId(response.data.data.id);
      }
    },
    onError: (error) => {
      setMessages(error.response?.data.errors?.base || ['Ocorreu um erro ao criar a negociação']);
    },
  });

  const formMethods = useForm<NegotiationValidationSchema>({
    resolver: zodResolver(negotiationSchema),
  });

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const watchNegotiationProducts = watch('negotiation_products_attributes');

  const onSubmit: SubmitHandler<NegotiationValidationSchema> = (formData) => createNegotiation(formData);

  useEffect(() => {
    if (!filterParams.establishment_id) {
      navigate('/negotiations');
    }
  }, []);

  return (
    <FormProvider {...formMethods}>
      <div>
        <div className="flex">
          <ShowHeader
            goBack
            goBackProps={{ fallback: '/negotiations', searchLastRouteFromFallback: true }}
            titleClassName="text-nowrap font-medium text-lg"
            title="Negociações"
            subtitle="Nova negociação"
            className="w-full"
            buttons={(
              <>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  isLoading={createIsPending}
                  disabled={!watchNegotiationProducts?.length}
                  data-tooltip-id="negotiation-disable-submit-tooltip"
                >
                  Enviar negociação
                </Button>

                {!watchNegotiationProducts?.length && (
                  <Tooltip id="negotiation-disable-submit-tooltip">
                    Selecione pelo menos um produto para enviar a negociação
                  </Tooltip>
                )}
              </>
            )}
          />
        </div>

        <Box className="flex gap-6 h-[calc(100vh-128px)] overflow-y-auto">
          <div className="w-1/2 flex flex-col gap-4 h-max">
            <div className="flex items-center gap-2">
              <ImageWithPlaceholder
                placeholderIcon={Building2Icon}
                className="size-6"
                iconClassName="size-4"
                url={establishment?.general_information?.company_logo?.url}
              />

              <p className="text-lg font-bold">
                {establishment?.general_information?.fantasy_name}
              </p>
            </div>

            <NegotiationHelper defaultOpen />

            <TextArea
              markAsRequired
              label="Descrição da sua proposta"
              placeholder="Escreva sua proposta"
              {...register('observation')}
              errorMessage={errors.observation?.message || undefined}
              textareaClassName="min-h-[114px]"
              autoResize
            />

            <EquipmentCategorySummary />
          </div>

          <div className="w-1/2 flex flex-col gap-4 h-max">
            <NegotiatingProducts establishment={establishment} />

            <EstablishmentConsumptionEstimativeNote />
          </div>
        </Box>
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={createNegotiationIsError}
          onClose={() => {
            if (negotiationId) {
              navigate(`/negotiations/${negotiationId}`);
            }
          }}
        />
      )}
    </FormProvider>
  );
};

export default NegotiationsCreate;
